import { NetworkIcon } from 'elements/NetworkIcon';
import { NETWORK_IDS } from 'lib/constants';
import { useModal } from 'mui-modal-provider';
import { ReactNode, useCallback } from 'react';
import { SimpleConnectAccountModal } from './SimpleConnectAccountModal';

interface Props {
  authenticated?: boolean;
  network: number;
  defaultLabel?: string;
  onConnect?: () => void;
}

interface RenderButtonProps {
  children: ReactNode;
  label: string;
}

/** The publicly connectable networks excluding fb/ig */
const VALID_NETWORKS = [
  NETWORK_IDS.twitter,
  NETWORK_IDS.twitch,
  NETWORK_IDS.youtube,
  NETWORK_IDS.tiktok,
  NETWORK_IDS.threads,
  NETWORK_IDS.linkedin,
  NETWORK_IDS.instagram,
  NETWORK_IDS.facebook,
] as const;

// Move RenderButton outside the main component
const RenderButton = (
  { children, label, onButtonClick }: RenderButtonProps & { onButtonClick: () => void },
) => (
  <button
    type="button"
    className="flex justify-center items-center border px-2 py-1 rounded-lg shadow-sm"
    onClick={onButtonClick}
  >
    <span className="mr-2 font-semibold align-middle text-xs">
      {label || 'Update connection'}
    </span>
    {' '}
    {children}
  </button>
);

export const ConnectButton = ({
  authenticated, network, defaultLabel, onConnect,
}: Props) => {
  const { showModal } = useModal();

  // Move handleClick to useCallback
  const handleClick = useCallback(() => {
    const modal = showModal(SimpleConnectAccountModal, {
      action: authenticated ? 'Update' : 'Connect',
      network,
      onClose: () => {
        modal.destroy();
      },
      onConnect: () => {
        modal.destroy();
        onConnect?.();
      },
    });
  }, [authenticated, network, showModal, onConnect]);

  const label = defaultLabel || (authenticated ? 'Refresh' : 'Connect');

  if (VALID_NETWORKS.includes(network)) {
    if (!authenticated && !label) return null;

    return (
      <RenderButton label={label} onButtonClick={handleClick}>
        <NetworkIcon network={network} iconColor="color" />
      </RenderButton>
    );
  }

  return null;
};
